import React, { useState, useCallback } from 'react';
import { ShareModal } from './ShareModal';
import { getShareLink } from '../../api/notifications';
import { useLanguage } from '../../i18n/useLanguage';
import './ShareButton.css';

interface ShareButtonProps {
  postId: string;
  postTitle?: string;
  variant?: 'icon' | 'text' | 'full';
  label?: string;
  className?: string;
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  postId,
  postTitle,
  variant = 'icon',
  label,
  className = '',
}) => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  const handleShare = useCallback(async () => {
    if (!navigator.share) {
      setIsModalOpen(true);
      return;
    }

    setSharing(true);
    try {
      const data = await getShareLink(postId);
      await navigator.share({
        title: data.title || postTitle || 'I Think It\'s You',
        text: data.description || '',
        url: data.shareUrl,
      });
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setIsModalOpen(true);
      }
    } finally {
      setSharing(false);
    }
  }, [postId, postTitle]);

  const buttonClassName = `share-btn share-btn--${variant} ${className}`.trim();

  return (
    <>
      <button
        className={buttonClassName}
        onClick={handleShare}
        aria-label={t('post.actionShare')}
        title={t('post.actionShare')}
        disabled={sharing}
      >
        <svg
          className="share-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        {variant !== 'icon' && <span>{label || t('post.actionShare')}</span>}
      </button>

      {isModalOpen && (
        <ShareModal
          postId={postId}
          postTitle={postTitle}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default ShareButton;
